import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT, Var } from 'gt-react';
import { ArrowSquareOutIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { useListSchemaDefinitions } from 'src/api/rpc/hooks/core/useSchemaDefinitions';
import {
  QuickSchemaFieldsDialog,
  type QuickSchemaDefaults,
} from './QuickSchemaFieldsDialog';
import {
  schemaDefinitionFieldSummary,
  schemaDefinitionPickerOptions,
  selectedSchemaDefinitionOption,
} from './schemaDefinitionPickerOptions';

function SchemaDefinitionPickerActions({
  disabled,
  schemaId,
  onCreate,
}: {
  disabled: boolean;
  schemaId: string | null;
  onCreate: () => void;
}) {
  return (
    <>
      {disabled ? null : (
        <Button type="button" variant="outline" size="sm" onClick={onCreate}>
          <PlusIcon className="size-3.5" />
          <T context="button to create a schema definition from a picker">
            New Schema
          </T>
        </Button>
      )}
      {schemaId ? (
        <Button type="button" variant="outline" size="sm" asChild>
          <Link
            to="/settings/organization/schema-definitions/$schemaId"
            params={{ schemaId }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ArrowSquareOutIcon className="size-3.5" />
            <T context="button to open the selected schema definition">
              Open Schema
            </T>
          </Link>
        </Button>
      ) : null}
    </>
  );
}

function SchemaDefinitionPickerNote({
  isError,
  fieldSummary,
}: {
  isError: boolean;
  /** Null when no definition is selected. Empty when it has no fields yet. */
  fieldSummary: string | null;
}) {
  if (isError) {
    return (
      <p className="text-xs text-destructive">
        <T context="error loading schema definitions for a picker">
          Could not load schema definitions.
        </T>
      </p>
    );
  }
  if (fieldSummary === null) {
    return null;
  }
  if (fieldSummary.length === 0) {
    return (
      <p className="text-[11px] leading-snug text-muted-foreground">
        <T context="selected schema definition has no fields yet">
          This definition has no fields yet. Open it to add the contract.
        </T>
      </p>
    );
  }
  return (
    <p className="text-[11px] leading-snug text-muted-foreground">
      <T context="preview of the fields on the selected schema definition">
        Fields on this definition: <Var>{fieldSummary}</Var>
      </T>
    </p>
  );
}

/**
 * Searchable catalog of schema definitions, plus the same create-in-place
 * dialog used by HTTP triggers and file extraction. The closed control shows
 * the definition's name. Field types are not the options.
 */
export function SchemaDefinitionPicker({
  value,
  onChange,
  disabled,
  noneOption,
  createDefaults,
  createTitle,
}: {
  /** `noneOption.id` or a schema definition id. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  noneOption: SelectDropdownOption;
  createDefaults?: QuickSchemaDefaults;
  createTitle?: ReactNode;
}) {
  const gt = useGT();
  const { data: schemas, isError } = useListSchemaDefinitions({
    isActive: true,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const listReady = schemas !== undefined || isError;
  const options = schemaDefinitionPickerOptions(schemas ?? [], noneOption);
  const selected = selectedSchemaDefinitionOption(
    options,
    value,
    noneOption.id,
    listReady,
    {
      loading: gt('Loading…', {
        context: 'schema definition picker display while the catalog loads',
      }),
      unavailable: gt('Unavailable schema', {
        context:
          'schema definition picker display when the saved definition is not in the catalog',
      }),
    },
  );
  const selectedSchema = schemas?.find((schema) => schema.id === value) ?? null;
  const fieldSummary = selectedSchema
    ? schemaDefinitionFieldSummary(selectedSchema)
    : '';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-56 flex-1">
          <SelectDropdown
            options={options}
            value={selected}
            onChange={(option) => onChange(option?.id ?? noneOption.id)}
            placeholder={gt('Search schema definitions…', {
              context: 'placeholder for a schema definition picker',
            })}
            searchPlaceholder={gt('Search…', {
              context: 'search box in a schema definition picker',
            })}
            size="sm"
            fullWidth
            disabled={disabled || !listReady}
          />
        </div>
        <SchemaDefinitionPickerActions
          disabled={disabled === true}
          schemaId={selectedSchema?.id ?? null}
          onCreate={() => setCreateOpen(true)}
        />
      </div>
      <SchemaDefinitionPickerNote
        isError={isError}
        fieldSummary={selectedSchema ? fieldSummary : null}
      />
      <QuickSchemaFieldsDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={createTitle}
        defaults={createDefaults}
        onComplete={(result) => onChange(result.schemaDefinitionId)}
      />
    </div>
  );
}
